import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, ViewChild, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PagesService } from '../pages.service';
import { Observable } from 'rxjs';
import { homePageTranslationsEn } from '../../../assets/translations/en/homePage';
import { homePageTranslationsFr } from '../../../assets/translations/fr/homePage';
import { getCurrentLanguage } from '../../core/i18n/translation-locale';
import { formatDateTimeForDisplay, formatIsoDateForDisplay, splitRawDateTime } from '../../core/utils/date-format.util';
import { JjsvTable, TableSortChange, TableSortDirection } from '../../components/jjsv-table/jjsv-table.component';
import { JjsvBanner } from "../../components/jjsv-banner/jjsv-banner.component";
import { JjsvLoader } from '../../components/jjsv-loader/jjsv-loader.component';
import { PurchaseOrderApiStatus, PurchaseOrderFilters, PurchaseOrderRow, PurchaseOrderStatusCell } from '../pages.models';

const STATUS_OPTIONS = [
  'Ready for review',
  'Awaiting consignment review',
  'Currently under review',
  'Review pending completion',
  'Conflicting shipto',
  'No order data found',
  'Completed',
  'Rejected',
];

const FR_ONLY_EXCLUDED_STATUSES = [
  'Awaiting consignment review'
];

const STATUS_FILTER_ALIASES: Record<string, string[]> = {
  'ready for review': ['ready for review'],
  'awaiting consignment review': ['awaiting consignment review', 'awaiting fix'],
  'currently under review': ['currently under review', 'in review'],
  'review pending completion': ['review pending completion', 'review in progress'],
  'Conflicting shipto': ['Conflicting shipto'],
  'no order data found': ['no order data found'],
  completed: ['completed'],
  rejected: ['rejected'],
};

const CLOSED_ORDER_STATUSES = ['Completed', 'Rejected'];
const OPEN_ORDER_STATUSES = STATUS_OPTIONS.filter(
  (status) => !CLOSED_ORDER_STATUSES.includes(status)
);
const DEFAULT_SEARCH_RANGE_MONTHS = 6;

// Formats dates for native input[type="date"] controls (yyyy-MM-dd).
function formatDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

// Default filter state uses homepage-configured search range, with a 6-month fallback.
function createDefaultFilters(searchRangeMonths: number = DEFAULT_SEARCH_RANGE_MONTHS): PurchaseOrderFilters {
  const today = new Date();
  const rangeStart = new Date(today);
  rangeStart.setMonth(rangeStart.getMonth() - searchRangeMonths);

  return {
    po: '',
    shipTo: '',
    requestedFrom: formatDateForInput(rangeStart),
    requestedTo: formatDateForInput(today),
  };
}

function resolveSearchRangeMonths(value: unknown): number {
  const parsedRange = Number.parseInt(String(value ?? ''), 10);
  if (!Number.isFinite(parsedRange) || parsedRange <= 0) {
    return DEFAULT_SEARCH_RANGE_MONTHS;
  }

  return parsedRange;
}

@Component({
  selector: 'app-purchase-orders-page',
  imports: [JjsvTable, JjsvBanner, JjsvLoader, CommonModule, FormsModule],
  templateUrl: './purchase-orders-page.component.html',
  styleUrl: './purchase-orders-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class PurchaseOrdersPage {
  private readonly pagesService = inject(PagesService);
  private readonly destroyRef = inject(DestroyRef);
  private selectedTileTitle: string | null = null;

  isSessionExpired$: Observable<boolean>;
  readonly homeComponentData$: Observable<any> = this.pagesService.getHomeComponentData();
  private readonly language = getCurrentLanguage();
  readonly translations = this.language === 'fr' ? homePageTranslationsFr : homePageTranslationsEn;
  statusOptions = [...STATUS_OPTIONS];
  private allowedSalesOrg: string[] | null = null;

  bannerContent = {
    title: 'Purchase Orders',
    subtitle: 'Use filters to find specific requests, check validation status, and take appropriate action.',
  };

  readonly tableColumns = [
    { key: 'requestDate', header: 'Request date and time', width: '10%' },
    { key: 'shipTo', header: 'Ship-to', width: '18%' },
    { key: 'poNumber', header: 'PO#', width: '7%' },
    { key: 'beingReviewedBy', header: 'Being reviewed by', width: '14%' },
    { key: 'reviewedBy', header: 'Reviewed by', width: '5%' },
    { key: 'completedDate', header: 'Date and time completed', width: '10%' },
    { key: 'comment', header: 'Comment', width: '20%' },
    { key: 'status', header: '', width: '16%' },
  ];

  readonly pageSizeOptions = [10, 20, 50];

  readonly searchRangeMonths = signal<number>(DEFAULT_SEARCH_RANGE_MONTHS);
  readonly timeZone = signal<string>('');

  // Source-of-truth UI state managed with signals.
  readonly filters = signal<PurchaseOrderFilters>(createDefaultFilters(this.searchRangeMonths()));

  readonly dateRange = computed(() => {
    const { requestedFrom, requestedTo } = createDefaultFilters(this.searchRangeMonths());
    return { min: requestedFrom, max: requestedTo };
  });
  readonly selectedStatuses = signal<string[]>([...STATUS_OPTIONS]);
  readonly isStatusDropdownOpen = signal<boolean>(false);
  readonly pageSize = signal<number>(10);
  readonly currentPage = signal<number>(1);
  readonly allRows = signal<PurchaseOrderRow[]>([]);
  readonly isLoading = signal<boolean>(false);
  readonly sortColumn = signal<string | null>('requestDate');
  readonly sortDirection = signal<TableSortDirection>('asc');
  readonly isManualSort = signal<boolean>(false);
  @ViewChild('statusDropdownContainer') private statusDropdownContainer?: ElementRef<HTMLElement>;

  // Data pipeline: raw rows -> filtered rows -> sorted rows -> paged rows.
  readonly filteredRows = computed(() => {
    const criteria = this.filters();
    const selectedStatusAliases = this.selectedStatuses().flatMap((status) => {
      const normalized = status.toLowerCase();
      return STATUS_FILTER_ALIASES[normalized] ?? [normalized];
    });
    const selectedStatusSet = new Set(selectedStatusAliases);

    return this.allRows().filter((row) => {
      const poValue = this.readString(row, 'poNumber').toLowerCase();
      const shipToValue = this.readString(row, 'shipTo').toLowerCase();
      const requestDateValue = this.toIsoDate(this.readString(row, 'requestDate'));
      const statusValue = this.getStatusText(row['status']).toLowerCase();

      const matchesPo = !criteria.po || poValue.includes(criteria.po.toLowerCase());
      const matchesShipTo = !criteria.shipTo || shipToValue.includes(criteria.shipTo.toLowerCase());
      const matchesFrom = !criteria.requestedFrom || requestDateValue >= criteria.requestedFrom;
      const matchesTo = !criteria.requestedTo || requestDateValue <= criteria.requestedTo;
      const matchesStatus = selectedStatusSet.has(statusValue);

      return matchesPo && matchesShipTo && matchesFrom && matchesTo && matchesStatus;
    });
  });

  readonly sortedRows = computed(() => {
    const rows = [...this.filteredRows()];
    const activeColumn = this.sortColumn();
    const activeDirection = this.sortDirection();

    if (!activeColumn || !activeDirection) {
      return rows;
    }

    // Uppercase because row statuses are converted to uppercase below
    const closedStatuses = ['REJECTED', 'COMPLETED'];
    const directionMultiplier = activeDirection === 'asc' ? 1 : -1;

    const sortedResult = rows.sort((left, right) => {
      const leftStatus = String(left['status']?.['text'] ?? '')
      .trim()
      .toUpperCase();

      const rightStatus = String(right['status']?.['text'] ?? '')
      .trim()
      .toUpperCase();

      const leftIsClosed = closedStatuses.includes(leftStatus);
      const rightIsClosed = closedStatuses.includes(rightStatus);

      // Apply pending priority for request-date sorting
      if (activeColumn === 'requestDate' && !this.isManualSort()) {
        // Pending row comes before Completed/Rejected row
        if (leftIsClosed !== rightIsClosed) {
          const priorityResult = leftIsClosed ? 1 : -1;
          return priorityResult;
        }
      }

      const comparisonResult =
        this.compareColumnValues(left, right, activeColumn) *
        directionMultiplier;
      return comparisonResult;
    });
    return sortedResult;
  });

  

  // Display-only copy of the visible rows; raw rows stay untouched for sorting/filtering.
  readonly displayRows = computed(() => {
    const zone = this.timeZone();
    return this.visibleRows().map((row) => ({
      ...row,
      requestDate: formatDateTimeForDisplay(this.readString(row, 'requestDate'), zone),
      completedDate: formatDateTimeForDisplay(this.readString(row, 'completedDate'), zone),
    }));
  });

  formatFilterDate(value: string): string {
    return formatIsoDateForDisplay(value, this.timeZone());
  }

  readonly totalResults = computed(() => this.sortedRows().length);

  readonly totalPages = computed(() => {
    const pages = Math.ceil(this.totalResults() / this.pageSize());
    return pages > 0 ? pages : 1;
  });
  
  readonly visibleRows = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    const end = start + this.pageSize();
    const paginatedRows = this.sortedRows().slice(start, end);
    return paginatedRows;
  });
  readonly displayRangeStart = computed(() => {
    if (this.totalResults() === 0) {
      return 0;
    }
    return (this.currentPage() - 1) * this.pageSize() + 1;
  });

  readonly displayRangeEnd = computed(() => {
    return Math.min(this.currentPage() * this.pageSize(), this.totalResults());
  });

  readonly paginationModel = computed<(number | '...')[]>(() => {
    const total = this.totalPages();
    const current = this.currentPage();
    if (total <= 7) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }

    if (current <= 4) {
      return [1, 2, 3, 4, '...', total];
    }

    if (current >= total - 3) {
      return [1, '...', total - 3, total - 2, total - 1, total];
    }

    return [1, '...', current - 1, current, current + 1, '...', total];
  });

  navLinks = [
    { label: this.translations.homeNavLabel, path: '/homepage', isActive: false },
    { label: this.translations.purchaseOrdersNavLabel, path: '/purchase-orders', isActive: true },
  ];

  navContent = {
    link: this.navLinks,
    loginStatus: true,
    subNavLinks: [
      { label: 'Purchase Orders', path: '/purchase-orders', isActive: true },
    ],
  };

  /** Bootstraps nav state, resolves tile context from session, and triggers the initial data load. */
  ngOnInit() {
    // Keep header/nav state synchronized with this page context.
    this.pagesService.updateNavigationNodes(this.navContent);
    this.isSessionExpired$ = this.pagesService.isSessionExpired$;
    this.selectedTileTitle = this.pagesService.getPurchaseOrderTitle();
    this.statusOptions = [...STATUS_OPTIONS];
    this.selectedStatuses.set(this.getDefaultStatusesForCurrentView());

    this.homeComponentData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.timeZone.set(data?.timeZone ?? '');
        const searchRangeMonths = resolveSearchRangeMonths(data?.searchRange);
        this.searchRangeMonths.set(searchRangeMonths);
        this.filters.set(createDefaultFilters(searchRangeMonths));
      });

    this.loadPurchaseOrderRows();
  }
  /** Fetches all purchase order rows; tile/status filtering is applied on the client. */
  private loadPurchaseOrderRows(): void {
    this.isLoading.set(true);
    this.pagesService
      .getPurchaseOrders()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          // API payload can be partially shaped; normalize into stable table rows.
          const rows = Array.isArray(data?.response) ? data.response : [];
          this.allowedSalesOrg = Array.isArray(data?.allowedSalesOrg)
            ? data.allowedSalesOrg.filter((salesOrg): salesOrg is string => typeof salesOrg === 'string')
            : null;
          this.statusOptions = this.getStatusOptionsForSalesOrg();
          this.selectedStatuses.set(
            this.getDefaultStatusesForCurrentView().filter((status) => this.statusOptions.includes(status))
          );
          this.allRows.set(rows.map((row) => this.normalizeRow(row)));
          this.currentPage.set(1);
          this.isLoading.set(false);
        },
        error: () => {
          this.allRows.set([]);
          this.currentPage.set(1);
          this.isLoading.set(false);
        },
      });
  }

  /** Updates one filter field from a form input event and resets pagination to page 1. */
  onFilterInput(field: keyof PurchaseOrderFilters, event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.filters.update((state) => ({ ...state, [field]: value }));
    this.currentPage.set(1);
  }

  /** Toggles a status in the active filter set and resets pagination to page 1. */
  toggleStatus(status: string): void {
    this.selectedStatuses.update((current) => {
      if (current.includes(status)) {
        return current.filter((item) => item !== status);
      }
      return [...current, status];
    });
    this.currentPage.set(1);
  }

  /** Resets filters, tile context, and statuses to "All Orders" defaults, then re-fetches from backend. */
  clearFilters(): void {
    this.selectedTileTitle = null;
    this.pagesService.setPurchaseOrderTitle(null);
    this.statusOptions = this.getStatusOptionsForSalesOrg();
    this.filters.set(createDefaultFilters(this.searchRangeMonths()));
    this.selectedStatuses.set([...this.statusOptions]);
    this.isStatusDropdownOpen.set(false);
    this.currentPage.set(1);
    // Ensure state updates complete before fetching
    setTimeout(() => this.loadPurchaseOrderRows(), 0);
  }

  /** Toggles the status multi-select dropdown open/closed. */
  toggleStatusDropdown(): void {
    this.isStatusDropdownOpen.update((state) => !state);
  }

  /** Closes the status dropdown unconditionally. */
  closeStatusDropdown(): void {
    this.isStatusDropdownOpen.set(false);
  }

  /** Closes the status dropdown on any click that falls outside its container element. */
  onDocumentClick(event: Event): void {
    if (!this.isStatusDropdownOpen()) {
      return;
    }

    const container = this.statusDropdownContainer?.nativeElement;
    const target = event.target as Node | null;

    if (!container || !target) {
      this.closeStatusDropdown();
      return;
    }

    if (!container.contains(target)) {
      this.closeStatusDropdown();
    }
  }

  /** Updates rows-per-page from the select event and resets pagination to page 1. */
  setPageSize(event: Event): void {
    const value = Number((event.target as HTMLSelectElement | null)?.value ?? 10);
    this.pageSize.set(value);
    this.currentPage.set(1);
  }

  /** Updates the active sort column and direction, then resets to page 1. */
  onSortChange(event: TableSortChange): void {
    // Reset to first page so users always see start of sorted results.
    this.isManualSort.set(true);
    this.sortColumn.set(event.column);
    this.sortDirection.set(event.direction);
    this.currentPage.set(1);
  }

  /** Navigates to the specified page; ignores values outside the valid range. */
  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages()) {
      return;
    }
    this.currentPage.set(page);
  }

  /** Navigates to the previous page of the table. */
  goToPreviousPage(): void {
    this.goToPage(this.currentPage() - 1);
  }

  /** Navigates to the next page of the table. */
  goToNextPage(): void {
    this.goToPage(this.currentPage() + 1);
  }

  /** Navigates to the previous browser history entry. */
  goBack(): void {
    history.back();
  }

  /** Re-fetches purchase order rows using the current tile context without changing filters. */
  refreshTableData(): void {
    this.loadPurchaseOrderRows();
  }

  /** Returns true if every available status option is currently selected. */
  isAllStatusesSelected(): boolean {
    return this.statusOptions.every(s => this.selectedStatuses().includes(s));
  }

  /** Selects all statuses when none/partial are selected; deselects all when all are selected. */
  toggleAllStatuses(): void {
    if (this.isAllStatusesSelected()) {
      this.selectedStatuses.set([]);
    } else {
      this.selectedStatuses.set([...this.statusOptions]);
    }
    this.currentPage.set(1);
  }

  /** Returns true if the given status string is currently in the active filter selection. */
  isStatusSelected(status: string): boolean {
    return this.selectedStatuses().includes(status);
  }

  /** Returns true if the given pagination token matches the currently active page. */
  isPageActive(page: number | '...'): boolean {
    return page === this.currentPage();
  }

  /** Returns default checked statuses for the active tile context. */
  private getDefaultStatusesForCurrentView(): string[] {
    const normalizedTitle = this.selectedTileTitle?.trim().toLowerCase() ?? '';

    if (normalizedTitle.includes('closed')) {
      return [...CLOSED_ORDER_STATUSES];
    }

    if (normalizedTitle.includes('open')) {
      return [...OPEN_ORDER_STATUSES];
    }

    return [...STATUS_OPTIONS];
  }

  private getStatusOptionsForSalesOrg(): string[] {
    const salesOrganizations = this.allowedSalesOrg?.map((salesOrg) => salesOrg.trim().toUpperCase()) ?? [];
    const isFranceOnly = salesOrganizations.length > 0 && salesOrganizations.every((salesOrg) => salesOrg === 'FR');
    
    return isFranceOnly
      ? STATUS_OPTIONS.filter((status) => !FR_ONLY_EXCLUDED_STATUSES.includes(status))
      : [...STATUS_OPTIONS];
  }

  /** Extracts a string value from a row cell, handling both plain strings and status objects. */
  private readString(row: PurchaseOrderRow, key: string): string {
    const value = row[key];
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'object' && value !== null && 'text' in value) {
      return String(value.text);
    }
    return '';
  }

  /** Converts dd-MM-yyyy, HH:mm to yyyy-MM-dd for range filter comparisons. */
  private toIsoDate(requestDateTime: string): string {
    const parts = splitRawDateTime(requestDateTime);
    return parts ? `${parts.year}-${parts.month}-${parts.day}` : '';
  }

  /** Normalizes an unknown API row into the table's typed PurchaseOrderRow shape. */
  private normalizeRow(row: Record<string, unknown>): PurchaseOrderRow {
    return {
      requestDate: this.toRowString(row['requestDate']),
      shipTo: this.toRowString(row['shipTo']),
      poNumber: this.toRowString(row['poNumber']),
      cartId: this.toRowString(row['cartId']),
      beingReviewedBy: this.toRowString(row['beingReviewedBy']),
      reviewedBy: this.toRowString(row['reviewedBy']),
      completedDate: this.toRowString(row['completedDate']),
      comment: this.toRowString(row['comment']),
      status: this.toStatusCell(row['status']),
    };
  }

  /** Compares two rows for sorting; uses date math for requestDate, localeCompare for all others. */
  private compareColumnValues(left: PurchaseOrderRow, right: PurchaseOrderRow, column: string): number {
    if (column === 'requestDate' || column === 'completedDate') {
      return this.parseRequestDate(this.readString(left, column)) - this.parseRequestDate(this.readString(right, column));
    }

    if (column === 'status') {
      const leftStatus = this.getStatusText(left['status']).toLowerCase();
      const rightStatus = this.getStatusText(right['status']).toLowerCase();
      return leftStatus.localeCompare(rightStatus);
    }

    const leftValue = this.readString(left, column).toLowerCase();
    const rightValue = this.readString(right, column).toLowerCase();
    return leftValue.localeCompare(rightValue);
  }

  /** Parses a dd-MM-yyyy, HH:mm date string into epoch milliseconds for sort comparisons. */
  private parseRequestDate(value: string | null | undefined): number {
    const trimmedValue = (value ?? '').trim();
    if (!trimmedValue) {
      return 0;
    }

    const rawParts = splitRawDateTime(trimmedValue);
    const [day, month, year] = [Number(rawParts?.day), Number(rawParts?.month), Number(rawParts?.year)];
    const timePart = rawParts?.time ?? '';
    const [hours, minutes] = timePart ? timePart.split(':').map((token) => Number(token)) : [0, 0];

    if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year) || day === 0 || month === 0 || year === 0) {
      return 0;
    }

    const normalizedHours = Number.isFinite(hours) ? hours : 0;
    const normalizedMinutes = Number.isFinite(minutes) ? minutes : 0;
    return new Date(year, month - 1, day, normalizedHours, normalizedMinutes).getTime();
  }

  /** Reads the text label from either a plain string or a PurchaseOrderStatusCell object. */
  private getStatusText(status: string | PurchaseOrderStatusCell): string {
    return typeof status === 'string' ? status : status.text;
  }

  /** Converts a raw API status value into the PurchaseOrderStatusCell shape; active is false when inReview. */
  private toStatusCell(value: unknown): string | PurchaseOrderStatusCell {
    if (typeof value === 'string') {
      return value;
    }

    if (this.isApiStatus(value)) {
      return {
        text: value.text,
        active: !(value.inReview ?? false),
      };
    }

    return '';
  }

  /** Type guard that checks whether a value conforms to the PurchaseOrderApiStatus contract. */
  private isApiStatus(value: unknown): value is PurchaseOrderApiStatus {
    if (typeof value !== 'object' || value === null) {
      return false;
    }

    return typeof (value as PurchaseOrderApiStatus).text === 'string';
  }

  /** Coerces null or undefined API values to an empty string; converts all others to string. */
  private toRowString(value: unknown): string {
    return value === null || value === undefined ? '' : String(value);
  }
}
